import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';
import { useActiveContextReady } from './useActiveContextReady';
import { referencedIds } from '../utils/credentialReferences';

/** What a credential reference in a stored config points at. Never its value. */
export interface ReferencedCredential {
  /** Null when you may not read the credential. */
  name: string | null;
  type: string;
  /** The version a restore writes. */
  version: number;
  /** Made from a device's config; nothing can use it. */
  placeholder: boolean;
  /** A placeholder holding a hash or encrypted value as the device wrote it. */
  raw: boolean;
  deleted: boolean;
}

/** The credentials `ids` name, by id. Ids of other organizations are absent. */
export function useCredentialReferences(ids: string[]) {
  const ready = useActiveContextReady();
  return useQuery<Record<string, ReferencedCredential>>({
    queryKey: ['credential-references', ids],
    queryFn: async () => {
      const res = await axios.get<{ credentials: Record<string, ReferencedCredential> }>(
        `${config.backendApi}/api/credentials/references`,
        { params: { ids: ids.join(',') }, withCredentials: true },
      );
      return res.data.credentials;
    },
    enabled: ready && ids.length > 0,
    staleTime: 60_000,
  });
}

/** The credentials a set of config texts references, for the lines that show them. */
export function useConfigTextCredentials(texts: (string | null | undefined)[]) {
  const { data } = useCredentialReferences(referencedIds(texts));
  return data ?? {};
}

/** A placeholder made from a device's config. Never usable, never its value. */
export interface ConfigCredential {
  id: string;
  name: string;
  regionId: string;
  divisionId: string | null;
  /** A hash or encrypted value as the device wrote it. */
  raw: boolean;
  deviceId: string | null;
  deviceName: string | null;
  /** Stored configs that use it, and of how many devices. */
  configs: number;
  devices: number;
  createdAt: string;
}

/** The placeholders made from device configs that you may read. */
export function useConfigCredentials(enabled: boolean) {
  const ready = useActiveContextReady();
  return useQuery<ConfigCredential[]>({
    queryKey: ['credentials-from-configs'],
    queryFn: async () => {
      const res = await axios.get<{ credentials: ConfigCredential[] }>(
        `${config.backendApi}/api/credentials/from-configs`,
        { withCredentials: true },
      );
      return res.data.credentials;
    },
    enabled: ready && enabled,
  });
}
